// Contracts — javelin: устройства модераторов для локального Session Packer.
//
// Модератор работает с отснятыми сессиями на клиентском макбуке локальным
// инструментом и тянет метаданные своих участников прямо из D1. Доступ
// привязан к КОНКРЕТНОЙ машине: при привязке она заводит неизвлекаемую
// пару ECDSA P-256 и присылает публичную половину; дальше каждый запрос
// подписан этим ключом.
//
// Worker: apps/api/src/javelin/devices.ts. Миграция: javelin/0041.
// Client: .private/session-pack-viewer (локальный инструмент, вне репо).
//
// Контракты здесь только на менеджерскую часть и на привязку. Сам
// подписанный запрос данных (deviceData) контракта запроса не имеет —
// он аутентифицируется заголовками, а не телом.
import { z } from 'zod';

// ── Менеджер выдаёт ПОСТОЯННЫЙ код привязки (migration 0044) ──────────
// Одноразовых кодов больше нет. Код многоразовый и бессрочный, двух
// типов: у каждого модератора свой ('mod') и админский ('all' — в
// инструменте выбор модератора, данные отдаются именно выбранного).
// Повторный запрос на ту же цель возвращает ТОТ ЖЕ код; rotate=true
// отзывает старый и заводит новый.
export const JavDeviceCodeIssueReqSchema = z.object({
  /**
   * 'mod'  — код модератора: привязывает машину к его данным.
   * 'all'  — админский: видно всех, модератор выбирается в инструменте.
   *          Выдаёт только PM.
   */
  scope: z.enum(['mod', 'all']).default('mod'),
  /** У админского кода пустой: область не привязана к модератору. */
  mod_id: z.string().default(''),
  label: z.string().max(80).default(''),
  /** Отозвать действующий код цели и завести новый. */
  rotate: z.boolean().default(false),
});
export type JavDeviceCodeIssueReq = z.infer<typeof JavDeviceCodeIssueReqSchema>;

export const JavDeviceCodeIssueResSchema = z.object({
  code: z.string(),
  scope: z.string().default('mod'),
  mod_id: z.string().default(''),
  /** true — вернули уже действующий код цели, ничего не создавалось. */
  existing: z.boolean().default(false),
});
export type JavDeviceCodeIssueRes = z.infer<typeof JavDeviceCodeIssueResSchema>;

// ── Список действующих постоянных кодов (менеджерский экран) ──────────
export const JavDeviceCodesListResSchema = z.object({
  codes: z.array(z.object({
    scope: z.string(),
    mod_id: z.string().default(''),
    display_name: z.string().default(''),
    code: z.string(),
    label: z.string().default(''),
    uses: z.number().default(0),
    created_at: z.string().default(''),
    last_used_at: z.string().default(''),
  })).default([]),
});
export type JavDeviceCodesListRes = z.infer<typeof JavDeviceCodesListResSchema>;

// ── Привязка устройства: код + публичный ключ → device_id ─────────────
// Единственный маршрут инструмента без подписи (подписывать ещё нечем).
export const JavDeviceEnrollReqSchema = z.object({
  code: z.string().min(4).max(40),
  /** Публичный ключ ECDSA P-256 в JWK (WebCrypto exportKey 'jwk'). */
  public_jwk: z.object({
    kty: z.literal('EC'),
    crv: z.literal('P-256'),
    x: z.string().min(1),
    y: z.string().min(1),
  }),
  label: z.string().max(80).default(''),
});
export type JavDeviceEnrollReq = z.infer<typeof JavDeviceEnrollReqSchema>;

export const JavDeviceEnrollResSchema = z.object({
  device_id: z.string(),
  scope: z.string().default('mod'),
  mod_id: z.string().default(''),
  display_name: z.string().default(''),
});
export type JavDeviceEnrollRes = z.infer<typeof JavDeviceEnrollResSchema>;

// ── Список устройств в кабинете ───────────────────────────────────────
export const JavDeviceRowSchema = z.object({
  device_id: z.string(),
  scope: z.string().default('mod'),
  mod_id: z.string().default(''),
  label: z.string().default(''),
  /** active | dormant | revoked. dormant считается на лету по last_seen_at. */
  status: z.string(),
  created_at: z.string().default(''),
  last_seen_at: z.string().default(''),
  requests: z.number().default(0),
  /** Дней с последнего запроса; null — ни одного запроса не было. */
  idle_days: z.number().nullable().default(null),
});
export type JavDeviceRow = z.infer<typeof JavDeviceRowSchema>;

export const JavDevicesListQuerySchema = z.object({
  mod_id: z.string().default(''),
});
export const JavDevicesListResSchema = z.object({
  devices: z.array(JavDeviceRowSchema).default([]),
  dormant_days: z.number(),
});
export type JavDevicesListRes = z.infer<typeof JavDevicesListResSchema>;

// ── Отзыв / пробуждение ───────────────────────────────────────────────
// «Разбудить» не выдаёт новый код: ключ на машине цел, сбрасывается
// только счётчик тишины. Отзыв — конец, нужна новая привязка.
export const JavDeviceActionReqSchema = z.object({
  device_id: z.string().min(1),
  action: z.enum(['revoke', 'wake']),
});
export type JavDeviceActionReq = z.infer<typeof JavDeviceActionReqSchema>;

export const JavDeviceActionResSchema = z.object({
  ok: z.boolean(),
  status: z.string(),
});

// ── Данные модератора для инструмента ─────────────────────────────────
// Тот же срез, что раньше собирал scripts/pull_d1.sh, но область считает
// сервер по строке устройства, а не клиент по параметру.
export const JavDeviceDataResSchema = z.object({
  /** Область устройства: 'mod' — один модератор, 'all' — мастер. */
  scope: z.string().default('mod'),
  /** Что реально отдано: у мастера пусто, когда отдали всех. */
  mod_id: z.string().default(''),
  display_name: z.string().default(''),
  /**
   * Все модераторы среза. У устройства модератора — ровно один; у
   * мастера — все, кого он видит. Сборщик пака берёт имя по mod_id
   * сессии отсюда, а не одно на весь файл.
   */
  moderators: z.array(z.object({
    mod_id: z.string(),
    display_name: z.string().default(''),
  })).default([]),
  generated_at: z.string(),
  /** Сколько часов инструменту разрешено жить на локальном кэше. */
  cache_ttl_h: z.number(),
  /** Рабочий диск модератора: инструмент встаёт на него сам. */
  backup_volume_uuid: z.string().default(''),
  backup_volume_name: z.string().default(''),
  participants: z.array(z.looseObject({})).default([]),
  locations: z.array(z.looseObject({})).default([]),
  exercises: z.array(z.looseObject({})).default([]),
  packs: z.array(z.looseObject({})).default([]),
  aggressor_codes: z.array(z.looseObject({})).default([]),
  /** Батчи, которых уже коснулась страница Трекера — их не доливаем (0050). */
  uploaded_batches: z.array(z.string()).default([]),
  /** Батчи, собранные инструментом (javelin_batches). */
  batches: z.array(z.looseObject({})).default([]),
});
export type JavDeviceDataRes = z.infer<typeof JavDeviceDataResSchema>;

// ── Отмена сессии из инструмента ──────────────────────────────────────
// Модератор выставляет только Cancelled и только пока менеджер не решил.
// Решение (Accepted / Pending / Rejected) приезжает из таблицы кроном и
// запирает отмену — сравнение через canonTracker, а не по строке.
export const JavDeviceCancelReqSchema = z.object({
  participant_id: z.string().min(1),
});
export type JavDeviceCancelReq = z.infer<typeof JavDeviceCancelReqSchema>;

export const JavDeviceCancelResSchema = z.object({
  ok: z.boolean(),
  participant_id: z.string(),
  tracker_status: z.string(),
});

// ── Упаковка: собрать пак билдером и отдать инструменту ───────────────
// Локальной сборки пака нет: единственный источник — buildV4Pack в
// воркере. Инструмент только кладёт полученный JSON рядом с сессией.
export const JavDevicePackReqSchema = z.object({
  participant_id: z.string().min(1),
  /** true — прогнать через билдер заново; false — отдать последний собранный. */
  rebuild: z.boolean().default(true),
  camera_refresh: z.looseObject({}).optional(),
  picked_codes: z.array(z.string()).optional(),
  /** Для разбитой съёмки: какая из частей главная. */
  main_session_id: z.string().optional(),
});
export type JavDevicePackReq = z.infer<typeof JavDevicePackReqSchema>;

export const JavDevicePackResSchema = z.object({
  ok: z.boolean(),
  participant_id: z.string(),
  /** Имя файла из R2 — под ним пак кладётся рядом с сессией. */
  file_name: z.string(),
  packed_at: z.string().default(''),
  json: z.string(),
});

// ── Правки участника / локации / упражнений из инструмента ────────────
// Белый список колонок ЖЁСТКИЙ и живёт в воркере: «обнови любую колонку»
// из подписанного клиента — это mass assignment, а не гибкость.
// Ключи здесь — имена колонок D1, а не ключи упаковочного JSON: карту
// «ключ пака → колонка» держит инструмент (scripts/apply_to_d1.py), он же
// нормализует значения к канону.
export const JavDeviceEditsReqSchema = z.object({
  participant_id: z.string().min(1),
  /** Колонки participants (белый список на стороне воркера). */
  participant: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
  /** Колонки locations. Правка локации задевает ВСЕХ её участников. */
  location: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
  /** Упражнения по индексу 0..18. */
  exercises: z.array(z.object({
    exercise_idx: z.number().int().min(0).max(18),
    values: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
  })).default([]),
});
export type JavDeviceEditsReq = z.infer<typeof JavDeviceEditsReqSchema>;

export const JavDeviceEditsResSchema = z.object({
  ok: z.boolean(),
  participant_id: z.string(),
  /** Идентификатор снимка «до» — по нему делается откат. */
  backup_id: z.number(),
  applied: z.object({
    participant: z.number().default(0),
    location: z.number().default(0),
    exercises: z.number().default(0),
  }),
  /** Колонки, которые прислали, но белый список не пропустил. */
  ignored: z.array(z.string()).default([]),
});

// ── Рабочий диск модератора ───────────────────────────────────────────
// Инструмент сам встаёт на диск модератора, а не спрашивает папку каждый
// раз. Опознание по UUID ТОМА: имя переименуется из Finder за секунду, и
// привязка сломается молча.
export const JavDeviceDiskReqSchema = z.object({
  /** UUID тома (diskutil VolumeUUID). Пусто — снять привязку. */
  volume_uuid: z.string().max(64).default(''),
  /** Имя тома — только для показа человеку. */
  volume_name: z.string().max(120).default(''),
});
export type JavDeviceDiskReq = z.infer<typeof JavDeviceDiskReqSchema>;

export const JavDeviceDiskResSchema = z.object({
  ok: z.boolean(),
  mod_id: z.string(),
  volume_uuid: z.string().default(''),
  volume_name: z.string().default(''),
});

// ── Обновление инструмента (index.html) ───────────────────────────────
// Инструмент живёт на машинах модераторов месяцами, а раздаётся архивом.
// Этот канал обновляет ТОЛЬКО index.html — код страницы, песочница
// браузера. serve.py / scripts / encrypt-tool так не обновляются: это
// процессы с доступом ко всем дискам, автозамена для них = RCE.
export const JavDeviceToolResSchema = z.object({
  published: z.boolean().default(false),
  /** Штамп сборки, с которым инструмент сравнивает свой. */
  version: z.string().default(''),
  size: z.number().default(0),
  sha256: z.string().default(''),
  notes: z.string().default(''),
  updated_at: z.string().default(''),
});
export type JavDeviceToolRes = z.infer<typeof JavDeviceToolResSchema>;

// ── Тикеты поддержки ──────────────────────────────────────────────────
// Три поля пишет человек; snapshot собирает сам инструмент. Смысл именно
// в snapshot: словами модератор проблему описывает плохо, а состояние
// машины описывает её точно. Отчёт recover здесь ТОЛЬКО ЧИТАЮЩИЙ — на
// создании тикета мы ничего не удаляем.
export const JavTicketSnapshotSchema = z.object({
  tool_version: z.string().max(40).default(''),
  browser:      z.string().max(200).default(''),
  volume_name:  z.string().max(120).default(''),
  volume_uuid:  z.string().max(64).default(''),
  free_gb:      z.number().nonnegative().default(0),
  batch:        z.string().max(120).default(''),
  /** Отчёт recover.py без --apply: сколько .encrypted, сирот, слоёв. */
  recover:      z.string().max(20000).default(''),
  /** Хвост encrypt-live.log — последние строки последнего прогона. */
  log_tail:     z.string().max(20000).default(''),
  /** Признак второго шифровальщика на той же папке (случай 28.08). */
  second_run:   z.boolean().default(false),
  notes:        z.string().max(2000).default(''),
});
export type JavTicketSnapshot = z.infer<typeof JavTicketSnapshotSchema>;

export const JavTicketCreateReqSchema = z.object({
  /** С чем столкнулись — единственное обязательное поле. */
  faced: z.string().min(3).max(4000),
  /** Как пробовали решить. */
  tried: z.string().max(4000).default(''),
  /** Каким видят идеальный исход. */
  ideal: z.string().max(4000).default(''),
  // Снимок целиком необязателен: диск могли отключить ровно сейчас, а
  // тикет всё равно должен уехать. Пустой объект добьётся дефолтами.
  snapshot: JavTicketSnapshotSchema.default(() => JavTicketSnapshotSchema.parse({})),
});
export type JavTicketCreateReq = z.infer<typeof JavTicketCreateReqSchema>;

export const JavTicketCreateResSchema = z.object({
  ok: z.boolean(),
  ticket_id: z.string(),
  delivered: z.number().int().nonnegative().default(0),
});
export type JavTicketCreateRes = z.infer<typeof JavTicketCreateResSchema>;

/** Лента тикетов модератора: инструмент показывает ответы поддержки. */
export const JavTicketListResSchema = z.object({
  tickets: z.array(z.object({
    ticket_id: z.string(),
    created_at: z.string(),
    status: z.string(),
    faced: z.string(),
    messages: z.array(z.object({
      ts: z.string(), author: z.string(), text: z.string(),
    })).default([]),
  })).default([]),
});
export type JavTicketListRes = z.infer<typeof JavTicketListResSchema>;

// ── Батчи и провалы чекера (0050) ─────────────────────────────────────
// «Создать batch» в инструменте: сессии подряд по номерам участников из
// пула зашифрованных. Запись заводится здесь и уезжает в трекер; пересоздание
// обновляет ту же запись — дубля не будет.
export const JavDeviceBatchReqSchema = z.object({
  batch: z.string().min(5).max(60),
  pids: z.array(z.string().regex(/^\d{6,12}$/)).min(1).max(50),
  sessions: z.array(z.object({ pid: z.string(), session: z.string().max(60) })).max(50).default([]),
});
export type JavDeviceBatchReq = z.infer<typeof JavDeviceBatchReqSchema>;
export const JavDeviceBatchResSchema = z.object({
  ok: z.boolean(),
  batch: z.string(),
  linked: z.number().int().nonnegative().default(0),
  unlinked: z.number().int().nonnegative().default(0),
  recreated: z.boolean().default(false),
});

/** Провал чекера заказчика → «To be fixed» + слепок; resolved — перепроверка прошла. */
export const JavDeviceProblemReqSchema = z.object({
  participant_id: z.string().regex(/^\d{6,12}$/),
  session: z.string().max(60).default(''),
  batch: z.string().max(60).default(''),
  errors: z.array(z.string().max(600)).max(60).default([]),
  snapshot: z.record(z.string(), z.unknown()).default({}),
  resolved: z.boolean().default(false),
});
export type JavDeviceProblemReq = z.infer<typeof JavDeviceProblemReqSchema>;
export const JavDeviceProblemResSchema = z.object({
  ok: z.boolean(),
  status: z.string().default(''),
  problem_id: z.number().int().optional(),
});
